export const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    icon: 'mdi:clock-outline',
    color: 'warning', // amber
    spinner: true,
    step: 0,
  },

  deposit_seen: {
    label: 'Detected',
    icon: 'mdi:eye-check-outline',
    color: 'info', // light blue
    spinner: true,
    step: 1,
  },

  deposit_processing: {
    label: 'Processing',
    icon: 'mdi:swap-horizontal',
    color: 'info', // light blue
    spinner: true,
    step: 1,
  },

  deposit_confirmed: {
    label: 'Deposit Confirmed',
    icon: 'mdi:check-decagram-outline',
    color: 'success', // green
    step: 2,
  },

  payout_queued: {
    label: 'Queued',
    icon: 'mdi:progress-clock',
    color: 'secondary', // purple → distinct from deposit
    spinner: true,
    step: 2,
  },

  payout_processing: {
    label: 'Sending',
    icon: 'mdi:swap-horizontal',
    color: 'primary', // strong blue (action)
    spinner: true,
    step: 2,
  },

  sent: {
    label: 'Completed',
    icon: 'mdi:check-circle',
    color: 'success', // green
    step: 3,
  },

  payout_failed: {
    label: 'Payout Failed',
    icon: 'mdi:alert-circle',
    color: 'error', // red
    step: 3,
  },

  payout_timed_out: {
    label: 'Payout Pending',
    icon: 'mdi:clock-alert-outline',
    color: 'warning',
    step: 3,
  },

  deposit_failed: {
    label: 'Deposit Failed',
    icon: 'mdi:alert-circle',
    color: 'warning', // orange → not fatal system error
    step: 1,
  },

  underpaid_refund_pending: {
    label: 'Amount Mismatch',
    icon: 'mdi:cash-refund',
    color: 'warning',
    spinner: true,
    step: 1,
  },

  overpaid_refund_pending: {
    label: 'Amount Mismatch',
    icon: 'mdi:cash-refund',
    color: 'warning',
    spinner: true,
    step: 1,
  },

  refunded: {
    label: 'Refunded',
    icon: 'mdi:cash-refund',
    color: 'info', // teal / calm
    step: 3,
  },

  refund_failed: {
    label: 'Refund Failed',
    icon: 'mdi:alert-circle',
    color: 'error', // red
    step: 3,
  },
};

// User-facing step order (single source of truth)
export const STEP_ORDER = ['pending', 'deposit_seen', 'deposit_confirmed', 'sent'];

// Statuses that render the progress stepper in its failed state.
export const FAILED_STATUSES = [
  'payout_failed',
  'deposit_failed',
  'refund_failed',
  'payout_timed_out',
];

// On-chain confirmations the backend waits for before moving a tx forward.
// MUST match the backend watcher thresholds (see backend change list).
export const REQUIRED_CONFIRMATIONS = {
  DEPOSIT: 10,
  PAYOUT: 10,
};

const DEPOSIT_CONFIRMING_STATUSES = ['deposit_seen', 'deposit_processing'];
const PAYOUT_CONFIRMING_STATUSES = ['payout_queued', 'payout_processing'];

// Helper: status → step index
export const getStepIndexFromStatus = (status) => {
  const config = STATUS_CONFIG[status];
  if (!config) return 0;
  return config.step;
};

export const isFailedStatus = (status) => FAILED_STATUSES.includes(status);

function clampConfirmations(value, max) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

/**
 * Confirmation counter shown next to a status, e.g. "3/10".
 * Returns null when no counter applies (fiat legs, final states, etc.).
 * Callers add their own brackets / suffix so existing layouts stay identical.
 */
export function getConfirmationProgress(tx) {
  if (!tx) return null;

  const { status, tokenIn, tokenOut } = tx;

  if (DEPOSIT_CONFIRMING_STATUSES.includes(status)) {
    if (tokenIn === 'dollar') return null;
    const done = clampConfirmations(tx.depositConfirmations, REQUIRED_CONFIRMATIONS.DEPOSIT);
    return `${done}/${REQUIRED_CONFIRMATIONS.DEPOSIT}`;
  }

  if (PAYOUT_CONFIRMING_STATUSES.includes(status)) {
    if (tokenOut === 'dollar') return null;
    const done = clampConfirmations(tx.payoutConfirmations, REQUIRED_CONFIRMATIONS.PAYOUT);
    return `${done}/${REQUIRED_CONFIRMATIONS.PAYOUT}`;
  }

  return null;
}

function toTimestamp(value) {
  if (!value) return null;

  let raw = value;
  if (typeof value === 'object' && value.$date) {
    raw = value.$date;
  }

  const ms = new Date(raw).getTime();
  if (Number.isNaN(ms)) return null;
  return ms;
}

/**
 * Guards against out-of-order updates (e.g. a slow REST response or a
 * reconnect replay arriving after a newer socket update). Returns false only
 * when BOTH copies carry updatedAt and the incoming one is strictly older.
 */
export function isIncomingTxFresh(existingTx, incomingTx) {
  const existingTs = toTimestamp(existingTx?.updatedAt);
  const incomingTs = toTimestamp(incomingTx?.updatedAt);

  if (existingTs === null || incomingTs === null) return true;
  return incomingTs >= existingTs;
}
